// @givmo/ai — the typed assistant SSE stream client. Mirrors the iOS
// `AssistantStreamClient` (`agent_docs/domain/assistant.md` § Transport), adapted
// to the web `fetch` streaming + `ReadableStream` reader.
//
// The flow (contract §1.1/§1.3):
//   1. POST a message → `202 SendMessageAccepted {turn_id, stream_path}` (the REST
//      side is a later/host concern; this client takes a `stream_path`).
//   2. GET `{stream_path}` with `Accept: text/event-stream`, replaying via
//      `Last-Event-ID: {turn_id}:{attempt}:{seq}` on every reconnect.
//   3. Parse the byte stream (`SseParser`), decode each event's `data:` payload to
//      a typed `AssistantStreamEvent`, and yield it.
//
// Reconnection + resume discipline (load-bearing, ported from iOS):
//   - Resume cursor = the last dispatched `id:` (`{turn_id}:{attempt}:{seq}`);
//     every reconnect sends it as `Last-Event-ID`.
//   - Terminal set `message_complete | turn_failed | turn_cancelled` finishes the
//     stream. A clean EOF BEFORE a terminal (the server's 150s cap) is NOT "done"
//     — it reconnects with the cursor.
//   - Forward-progress retry accounting: a clean EOF that dispatched ≥1 event is a
//     PLANNED 150s rotation → reconnect IMMEDIATELY (no backoff) and reset the
//     failure budget. Only a no-progress close or a transient error with no prior
//     progress counts toward `maxRetryAttempts`; a drop after progress resets the
//     budget. The budget bounds CONSECUTIVE no-progress failures, not the lifetime
//     of a healthy multi-window turn.
//   - Transient errors reconnect after a jittered exponential backoff via an
//     injected `sleep`/`random` (deterministic in tests — no wall-clock).
//   - `turn_restarted` (attempt supersession): an in-band control signal the server
//     emits when it restarts a turn at a higher attempt. The client adopts the new
//     attempt as the resume baseline (the prior attempt's cursor is superseded) and
//     surfaces it so the consumer can drop the stale partial reply.
//   - Heartbeat tolerance: the parser discards the 2048-byte pad + the 11s ping
//     comments, so they never reach this client.
//   - Crash-safety (§3.7): an unknown future `event:` name or a malformed `data:`
//     line is yielded as an `unknown` event / skipped — never thrown.
import { SseParser } from './parser';
import {
  isTerminalEventType,
  type AssistantStreamEvent,
  type MessageCompletePayload,
  type MessageStartPayload,
  type TextDeltaPayload,
  type ToolStatusPayload,
  type TurnCancelledPayload,
  type TurnFailedPayload,
} from '../contract/events';

/** A control signal surfaced alongside the typed events — distinct from the 6
 * frozen-vocabulary events. Today only `turn_restarted` (attempt supersession). */
export interface TurnRestartedSignal {
  type: 'turn_restarted';
  /** The new attempt the server restarted at, if the payload carried one. */
  attempt?: number;
}

/** Everything a turn stream yields: a typed event or the supersession control. */
export type AssistantStreamYield = AssistantStreamEvent | TurnRestartedSignal;

/** Injectable deps so the client is fully deterministic under test. */
export interface AssistantStreamClientDeps {
  /** The base origin/URL the relative `stream_path` resolves against (the API
   * origin). Trailing slash optional. */
  baseUrl: string;
  /** `fetch` impl (defaults to the global). Tests inject a scripted fetch. */
  fetchImpl?: typeof fetch;
  /** Resolve the auth headers per request (bearer + App Check, etc.). Async so a
   * fresh token can be minted per (re)connect. */
  authHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  /** Sleep `ms` (defaults to `setTimeout`). Tests inject a controllable clock. */
  sleep?: (ms: number) => Promise<void>;
  /** `[0,1)` jitter source (defaults to `Math.random`). */
  random?: () => number;
  /** Max CONSECUTIVE no-progress reconnect attempts before giving up (default 6,
   * matching iOS). */
  maxRetryAttempts?: number;
  /** Base backoff ms (default 500). Backoff = base * 2^attempt, jittered ±50%,
   * capped at `maxBackoffMs`. */
  baseBackoffMs?: number;
  maxBackoffMs?: number;
}

const DEFAULT_MAX_RETRY = 6;
const DEFAULT_BASE_BACKOFF_MS = 500;
const DEFAULT_MAX_BACKOFF_MS = 8000;

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Thrown when the no-progress retry budget is exhausted, or a connection
 * returned a non-OK HTTP status. The originating value (an HTTP status, the
 * underlying transport error) rides on the standard `Error.cause`. */
export class AssistantStreamError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'AssistantStreamError';
  }
}

export class AssistantStreamClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly authHeaders: () => Promise<Record<string, string>> | Record<string, string>;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly random: () => number;
  private readonly maxRetryAttempts: number;
  private readonly baseBackoffMs: number;
  private readonly maxBackoffMs: number;

  /** The resume cursor (`{turn_id}:{attempt}:{seq}`) — persists across reconnects
   * AND across `stream()` calls for the same turn (scenePhase resume). A new turn
   * MUST `resetCursor()` first. */
  private cursor: string | undefined;

  private abortController: AbortController | undefined;

  /** Set by `teardown()`; checked in the stream loop + the catch so a deliberate
   * cancel is recognized as such (NOT misread as a transient error to reconnect).
   * Cleared at the start of each `stream()` so a torn-down client can re-stream. */
  private tornDown = false;

  constructor(deps: AssistantStreamClientDeps) {
    this.baseUrl = deps.baseUrl;
    this.fetchImpl = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.authHeaders = deps.authHeaders ?? (() => ({}));
    this.sleep = deps.sleep ?? defaultSleep;
    this.random = deps.random ?? Math.random;
    this.maxRetryAttempts = deps.maxRetryAttempts ?? DEFAULT_MAX_RETRY;
    this.baseBackoffMs = deps.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS;
    this.maxBackoffMs = deps.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS;
  }

  /** The current resume cursor (for inspection / persistence). */
  get lastEventId(): string | undefined {
    return this.cursor;
  }

  /** Clear the resume cursor — call before a genuinely NEW turn (NOT on a
   * scenePhase resume of the same in-flight turn). Mirrors iOS `resetCursor()`. */
  resetCursor(): void {
    this.cursor = undefined;
  }

  /** Cancel the in-flight stream WITHOUT clearing the cursor, so a later
   * `stream()` resumes via `Last-Event-ID`. Mirrors iOS `teardown()`. */
  teardown(): void {
    this.tornDown = true;
    this.abortController?.abort();
    this.abortController = undefined;
  }

  /**
   * Stream a turn. Yields typed events (+ the `turn_restarted` control) until a
   * terminal event, then returns. Reconnects transparently on planned 150s
   * rotations and transient drops, bounded by the no-progress budget.
   */
  async *stream(streamPath: string): AsyncGenerator<AssistantStreamYield, void, void> {
    const url = this.resolveUrl(streamPath);
    let noProgressAttempts = 0;
    // A fresh stream() clears any prior teardown so a reused client can re-stream.
    this.tornDown = false;

    while (true) {
      if (this.tornDown) return; // torn down between connections — end quietly
      this.abortController = new AbortController();
      let dispatchedThisConnection = 0;
      let terminalReached = false;

      try {
        const headers: Record<string, string> = {
          Accept: 'text/event-stream',
          ...(await this.authHeaders()),
        };
        if (this.cursor !== undefined) {
          headers['Last-Event-ID'] = this.cursor;
        }

        const response = await this.fetchImpl(url, {
          method: 'GET',
          headers,
          signal: this.abortController.signal,
        });

        if (!response.ok || response.body === null) {
          throw new AssistantStreamError(
            `assistant stream HTTP ${response.status}`,
            response.status,
          );
        }

        for await (const rawEvent of readSseEvents(response.body, this.cursor)) {
          // The consumer may have torn down while we were suspended at a yield.
          if (this.tornDown) {
            this.abortController = undefined;
            return;
          }
          // Persist the resume cursor on every event that carried an id.
          if (rawEvent.id !== undefined) this.cursor = rawEvent.id;

          const decoded = decodeRawEvent(rawEvent.event ?? 'message', rawEvent.data);
          if (decoded === undefined) continue; // malformed payload → skip (§3.7)

          dispatchedThisConnection += 1;

          // Attempt supersession: adopt the new attempt baseline. The prior
          // attempt's cursor is stale; clear it so the reconnect replays the
          // restarted attempt from the beginning.
          if (decoded.type === 'turn_restarted') {
            this.cursor = undefined;
            yield decoded;
            continue;
          }

          yield decoded;

          if (isTerminalEventType(decoded.type)) {
            terminalReached = true;
            break;
          }
        }

        if (terminalReached) {
          this.abortController = undefined;
          return;
        }

        // Clean EOF before a terminal = the server's 150s cap.
        if (dispatchedThisConnection > 0) {
          // Planned rotation: reconnect IMMEDIATELY, reset the failure budget.
          noProgressAttempts = 0;
          continue;
        }
        // No-progress close → counts toward the budget (fall through to backoff).
        noProgressAttempts += 1;
      } catch (error) {
        if (this.tornDown || this.abortController?.signal.aborted) {
          // Deliberate teardown — not a failure. End the generator quietly; the
          // cursor is preserved so a later stream() resumes. (teardown() nulls
          // `abortController`, so the `tornDown` flag is the reliable signal.)
          this.abortController = undefined;
          return;
        }
        // A drop AFTER progress resets the budget (a healthy turn shouldn't die
        // because one window flaked); a no-progress drop counts.
        if (dispatchedThisConnection > 0) {
          noProgressAttempts = 0;
        } else {
          noProgressAttempts += 1;
        }
        if (noProgressAttempts > this.maxRetryAttempts) {
          this.abortController = undefined;
          throw new AssistantStreamError('assistant stream retry budget exhausted', error);
        }
      }

      if (noProgressAttempts > this.maxRetryAttempts) {
        this.abortController = undefined;
        throw new AssistantStreamError('assistant stream retry budget exhausted');
      }

      // Backoff only for no-progress reconnects (a planned rotation `continue`d
      // above before reaching here).
      if (noProgressAttempts > 0) {
        await this.sleep(this.backoffMs(noProgressAttempts));
      }
    }
  }

  /** Jittered exponential backoff for the Nth (1-based) no-progress attempt. */
  private backoffMs(attempt: number): number {
    const exp = this.baseBackoffMs * 2 ** (attempt - 1);
    const capped = Math.min(exp, this.maxBackoffMs);
    // ±50% jitter.
    const jitter = capped * 0.5 * (this.random() * 2 - 1);
    return Math.max(0, Math.round(capped + jitter));
  }

  private resolveUrl(streamPath: string): string {
    if (/^https?:\/\//i.test(streamPath)) return streamPath;
    const base = this.baseUrl.replace(/\/+$/, '');
    const path = streamPath.startsWith('/') ? streamPath : `/${streamPath}`;
    return `${base}${path}`;
  }
}

/** Read a `ReadableStream<Uint8Array>` (the fetch body) as completed SSE events,
 * seeding the parser's last-id with the resume cursor. */
async function* readSseEvents(
  body: ReadableStream<Uint8Array>,
  resumeCursor: string | undefined,
): AsyncGenerator<{ id?: string; event?: string; data: string }, void, void> {
  const parser = new SseParser();
  if (resumeCursor !== undefined) parser.lastEventId = resumeCursor;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const event of parser.consume(chunk)) {
        yield event;
      }
    }
    // Flush any trailing buffered event (defensive — the contract ends events
    // with a blank line, but a clean close mid-buffer shouldn't drop a record).
    const tail = decoder.decode();
    if (tail.length > 0) {
      for (const event of parser.consume(tail)) {
        yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** Decode a raw SSE event (name + JSON `data`) to a typed stream yield. Returns
 * `undefined` when the payload is unparseable (skip; never throw — §3.7). A future
 * event name surfaces as an `unknown` event. */
export function decodeRawEvent(name: string, data: string): AssistantStreamYield | undefined {
  let payload: unknown;
  try {
    payload = data.length > 0 ? JSON.parse(data) : {};
  } catch {
    return undefined; // malformed JSON → skip
  }

  switch (name) {
    case 'message_start':
      return { type: 'message_start', payload: payload as MessageStartPayload };
    case 'tool_status':
      return { type: 'tool_status', payload: payload as ToolStatusPayload };
    case 'text_delta':
      return { type: 'text_delta', payload: payload as TextDeltaPayload };
    case 'message_complete':
      return { type: 'message_complete', payload: payload as MessageCompletePayload };
    case 'turn_failed':
      return { type: 'turn_failed', payload: payload as TurnFailedPayload };
    case 'turn_cancelled':
      return { type: 'turn_cancelled', payload: payload as TurnCancelledPayload };
    case 'turn_restarted': {
      const attempt =
        typeof (payload as { attempt?: unknown }).attempt === 'number'
          ? (payload as { attempt: number }).attempt
          : undefined;
      return { type: 'turn_restarted', attempt };
    }
    default:
      // A future event name — surface tolerantly so the consumer can skip it.
      return { type: 'unknown', name, payload };
  }
}
